import { ClientGroupMembershipStatusEnum } from '@DigitaleDoerfer/digitale-doerfer-api';
import { useCallback, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useGetAllGroupPosts } from '@/features/api/hooks/grapevine-group/useGetAllGroupPosts';
import { useGetGroup } from '@/features/api/hooks/grapevine-group/useGetGroup';
import { useJoinGroup } from '@/features/api/hooks/grapevine-group-events/useJoinGroup';
import { PostCreationContext } from '@/features/post-editor/types/postCreationOptions';
import { SimplePostList } from '@/features/posts/components/SimplePostList';
import { SIZE } from '@/features/posts/constants/params';
import { PostFeedType } from '@/features/posts/types/postFeed';
import { useVerification } from '@/features/verification/providers/useVerification';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { useAppNavigation } from '@/navigation/hooks/useAppNavigation';
import { AppRoute } from '@/navigation/types/navigation';
import { FloatingActionButton } from '@/shared/components/FloatingActionButton';

type GroupPostFeedProps = { groupId?: string };

export const GroupPostFeed: FC<GroupPostFeedProps> = ({ groupId = '' }) => {
  const { t } = useTranslation(LocalizationNameSpace.GROUPS, {
    keyPrefix: 'screens.group'
  });

  const { data: group, isFetched: isGroupFetched } = useGetGroup(groupId);

  const {
    data: posts,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    refetch,
    isRefetching
  } = useGetAllGroupPosts({
    groupId,
    count: SIZE
  });

  const joinGroup = useJoinGroup();

  const isApprovedMember =
    group?.membershipStatus === ClientGroupMembershipStatusEnum.Approved;

  const { ensureVerified } = useVerification();
  const navigation = useAppNavigation();

  const handleCreateGroupPost = useCallback(() => {
    if (!ensureVerified()) return;

    navigation.navigate(AppRoute.PostCreationHome, {
      channelType: PostFeedType.GOSSIP,
      creationContext: PostCreationContext.GRUPPE,
      groupId
    });
  }, [ensureVerified, navigation, groupId]);

  const handleJoinRequest = () => joinGroup.mutate({ groupId });

  const { bottom } = useSafeAreaInsets();

  return (
    <>
      <SimplePostList
        posts={posts}
        isLoading={isLoading}
        isError={isError}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetching={isFetching}
        isFetchingNextPage={isFetchingNextPage}
        refetch={refetch}
        isRefetching={isRefetching}
        groupId={groupId}
        hidePostType
        canCreatePost={isApprovedMember}
      />
      {isGroupFetched && (
        <FloatingActionButton
          testID="group-create-post-fab"
          onPress={isApprovedMember ? handleCreateGroupPost : handleJoinRequest}
          isLoading={joinGroup.isPending}
          isDisabled={joinGroup.isPending}
          label={isApprovedMember ? undefined : t('join')}
          bottom={bottom}
        />
      )}
    </>
  );
};
